<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import DefaultAvatar from '@/assets/default_avatar.png'
import { ImageLoadStatus, useImageLoad } from '@/store/imageLoad'

interface Props {
  isActive: boolean
  className?: string[]
  url: string | undefined
}

const props = withDefaults(defineProps<Props>(), {
  url: '',
})

const baseClass = ['absolute', 'bottom-0', 'responsive-image', 'standard-height']
if (props.className) {
  baseClass.push(...props.className)
}

const imageStore = useImageLoad()
const isImageLoaded = ref(false)
const preloadImage = new Image()

onMounted(() => {
  loadImage(props.url)
})

watch(() => props.url, (newUrl) => {
  isImageLoaded.value = false
  loadImage(newUrl)
})

function loadImage(url: string) {
  if (url === '') {
    isImageLoaded.value = true
    return
  }
  const status = imageStore.getImageLoadStatus(url)
  if (status !== ImageLoadStatus.Loaded) {
    // retry load image
    imageStore.loadImage(url)
  }

  preloadImage.src = url
  preloadImage.onload = () => {
    isImageLoaded.value = true
  }
}

onUnmounted(() => {
  // Clean up to avoid memory leaks
  preloadImage.onload = null
})
</script>

<template>
  <img
    v-if="isImageLoaded && props.url != null && props.url.length > 0"
    :class="`${[...baseClass, props.isActive ? 'active-image' : 'inactive-image'].join(' ')}`"
    :src="props.url === '' ? DefaultAvatar : props.url"
    alt=""
  >
  <!-- Placeholder or spinner for loading state -->
  <div v-else />
</template>
